// ============================================================================
// SPÉCIFICATION DU TOAST — la SOURCE UNIQUE de son design, pour TOUTES les plateformes.
//
// ⚠️ POURQUOI CE FICHIER EXISTE : le rendu d'un toast ne PEUT pas être partagé entre React Native et le
// DOM (`<View>` n'est pas un `<div>`, `Animated` n'est pas une transition CSS). Deux rendus sont donc
// inévitables. Ce qui l'est moins, c'est qu'ils DIVERGENT — et ils avaient divergé : pilule + icône +
// empilement côté tablette, rectangle + point de 8 px + UN SEUL toast à la fois côté web.
//
// La règle retenue (décision utilisateur 2026-08-03) est « même design des deux côtés, aligné à chaque
// modification esthétique ». Aligner À LA MAIN suppose que personne n'oublie jamais — or la doctrine du
// projet dit l'inverse : « ce qui dépend de la vigilance humaine finit par diverger » (§24.3bis B-2).
// Les valeurs de design vivent donc ICI, en DONNÉES, et les deux rendus les LISENT. Changer un rayon ou
// une durée se fait à un seul endroit, et les deux plateformes suivent sans qu'on ait à y penser.
//
// CE QUI EST ICI : géométrie, durées, animation, empilement, ton → icône.
// CE QUI N'Y EST PAS : les COULEURS. Chaque plateforme a déjà son thème (clair/sombre) ; y figer des
// valeurs casserait le mode sombre. Le rôle de chaque couleur est donc décrit dans `TOAST_COLOR_ROLES`
// et c'est le thème local qui le résout — les deux thèmes étant eux-mêmes tenus alignés.
// ============================================================================

/**
 * Les QUATRE tons, et seulement eux (SPEC §24.1quater B).
 * ⚠️ Vocabulaire unifié : la tablette disait `success`/`error`, le web `ok`/`err` — les mêmes concepts
 * sous deux noms. Un ton libre ou une couleur ad hoc n'existe pas : une nuance qui ne veut rien dire de
 * précis finit par ne rien dire du tout.
 */
export type ToastTone = 'ok' | 'info' | 'warn' | 'err';

export const TOAST_TONES: readonly ToastTone[] = ['ok', 'info', 'warn', 'err'] as const;

/**
 * Icône par ton. Les noms sont ceux des DEUX registres (pos-shell et web) — vérifiés présents des deux
 * côtés. ⚠️ Une icône, et pas seulement une couleur : la couleur seule ne porte pas l'information pour
 * qui la distingue mal, et un toast se lit en une seconde.
 */
import type { IconName } from './icons';

export const TOAST_ICON: Record<ToastTone, IconName> = {
  ok: 'check',
  info: 'info',
  warn: 'lock',
  err: 'x',
};

/** Géométrie de la pilule. Portée de la maquette (Sliderz Refonte.html) via le rendu tablette. */
export const TOAST_PILL = {
  radius: 999,        // pilule pleine, pas un rectangle arrondi
  padV: 12,
  padH: 20,
  gap: 10,            // icône ↔ texte
  maxWidth: 640,
  fontSize: 14,
  fontWeight: 500,
  iconSize: 16,
  iconStroke: 2.5,
  maxLines: 2,        // au-delà, le message est trop long pour un toast : c'est une alerte
} as const;

/** Ombre portée (mêmes valeurs traduites dans chaque moteur de rendu). */
export const TOAST_SHADOW = { blur: 16, offsetY: 12, opacity: 0.4 } as const;

/** Empilement et position. */
export const TOAST_STACK = {
  bottom: 28,   // ⚠️ la tablette y AJOUTE l'inset de zone sûre (barre de navigation Android)
  gap: 8,
  /**
   * Nombre de toasts visibles simultanément.
   * ⚠️ > 1 délibérément : le web n'en affichait qu'UN, et une deuxième action effaçait le message de la
   * première — l'opérateur perdait une information qu'il n'avait pas encore lue (§23.0).
   */
  maxVisible: 3,
} as const;

/**
 * COMMENT UN NOUVEAU MESSAGE COHABITE AVEC CEUX DÉJÀ À L'ÉCRAN.
 *
 * ⚠️ DEUX COMPORTEMENTS, UN SEUL TOAST (décision utilisateur 2026-09-04). Les deux ont leur emploi, et
 * la tentation était d'écrire un second composant pour le second besoin : deux files, deux rendus, deux
 * endroits où corriger la même chose. C'est une OPTION de l'appel, jamais un doublon.
 *
 * - `stack` (DÉFAUT) — les messages s'empilent jusqu'à `maxVisible`. C'est le comportement sûr : deux
 *   actions distinctes produisent deux retours, et aucun n'efface l'autre avant d'avoir été lu (§23.0).
 * - `replace` — le nouveau message prend la place de TOUS ceux affichés. Réservé à un GESTE RÉPÉTÉ dont
 *   seul le dernier retour compte : biper trente codes à la douchette empile trente pilules, dont
 *   vingt-neuf sont périmées avant d'être lues.
 *
 * ⚠️ CE QUE `replace` COÛTE, et pourquoi il n'est pas le défaut : il peut effacer un message qu'on
 * n'avait pas fini de lire. Il ne se choisit donc que là où le message SUIVANT dit la même chose en
 * mieux — le total d'après remplace le total d'avant. Partout ailleurs, empiler.
 */
export type ToastMode = 'stack' | 'replace';

export const TOAST_MODES: readonly ToastMode[] = ['stack', 'replace'] as const;

/** Le mode retenu quand l'appelant n'en demande pas. */
export const TOAST_DEFAULT_MODE: ToastMode = 'stack';

/** Durées. */
export const TOAST_TIMING = {
  defaultMs: 3200,
  /** Confirmations qui engagent (scellement fiscal §9.4.B) : laissées plus longtemps à l'écran. */
  longMs: 5000,
  enterMs: 200,
  enterScaleFrom: 0.85,
} as const;

/**
 * RÔLE de chaque couleur — résolu par le thème LOCAL de chaque plateforme (clair/sombre), jamais figé ici.
 * ⚠️ Contrat d'alignement : si les deux thèmes définissent ces rôles de la même façon, les deux rendus
 * sont visuellement identiques. C'est le seul point qui reste à tenir à la main, et il est nommé ici pour
 * qu'on sache exactement quoi vérifier lors d'un changement de charte.
 */
export const TOAST_COLOR_ROLES = {
  background: 'ink-950 (fond sombre, le plus dense de l’échelle)',
  text: 'paper (le contraste maximal sur ce fond)',
  icon: { ok: 'teal-500', info: 'ink-400', warn: 'amber (oklch 0.7 0.14 70 → #D58D25)', err: 'rose' },
} as const;
